import { getGroupById } from 'api/operations/groupService';
import { action, autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import DocumentDelegate from 'store/modules/upload-documents/documentDelegate';
import { UploadDocumentsModule } from 'store/modules/upload-documents/uploadDocumentsModule';
import { RequestDocumentModalStore } from 'store/modules/upload-documents/ui-stores/requestDocumentModalStore';
import { UploadDocumentModalStore } from 'store/modules/upload-documents/ui-stores/uploadDocumentModalStore';
import { UploadStep } from 'types/documentsService';
import { Group } from 'types/groupService';

export interface UploadDocumentsDomainData {
  // Usually enrollmentId, can be cartId
  entityId: string;
  // If it is present, then it's enrollmentId, and entityId is cartId.
  affiliatedId?: string;
}

interface PageCompletion {
  allowed: boolean;
  notAllowedMessage?: string;
}

/* This interfaces is implemented on pages that allow completion/uncompletion of their corresponding flow parts. */
export interface StepInteractor {
  completeStep: () => Promise<void>;
  uncompleteStep: () => Promise<void>;
  get thisStepHasBeenCompleted(): boolean;
}

export default abstract class UploadDocumentsPageStore {
  protected readonly _domainData: UploadDocumentsDomainData;
  protected readonly _enrollmentPeriodModule = new EnrollmentPeriodModule();
  protected _uploadDocumentsModule;
  protected _isLoading = false;
  protected _group: Pick<Group, 'name' | 'id'> | null = null;

  // Ui stores
  public uploadDocumentModalStore: UploadDocumentModalStore | null = null;
  public requestDocumentModalStore: RequestDocumentModalStore | null = null;

  public async loadInitialData(): Promise<void> {
    this._setIsLoading(true);
    await this._uploadEnrollmentData();

    /**
     * Fetch group info once at page load to avoid duplicate requests.
     * This group info is used by:
     * 1. UploadDocumentModalStore for file naming (groupName)
     * 2. Child page stores that need group info (via this.group getter)
     *
     * Note: Some pages have sidebars that make lazy queries for group data - those will be reworked over time.
     * Child stores should use this.group instead of fetching group data themselves.
     */

    await this._fetchGroupInfo(this._enrollmentPeriodModule.enrollmentCoreData?.groupId || '');

    await this._uploadDocumentsModule.loadInitialData();
    // Autorun ensures the mobx reactive context for the actions inside
    autorun(() => {
      this._createRequestDocumentModalStore();
      this._createDocumentUploadModalStore();
    });
    this._setIsLoading(false);
  }

  // Some pages don't use enrollment period, so we need to pass group id explicitly.
  protected async _fetchGroupInfo(groupId: string): Promise<void> {
    if (groupId && !this._group) {
      const group = await getGroupById(groupId);
      runInAction(() => {
        this._group = { name: group.name, id: group.id };
      });
    }
  }

  protected async _uploadEnrollmentData(): Promise<void> {
    await this._enrollmentPeriodModule.uploadEnrollmentPeriod(
      // When affiliated id is present, it means that it is enrollment id. Otherwise, entityId stores enrollmentId.
      // This logic may be overwritten by child classes if it gets more complicated than this.
      this._domainData.affiliatedId || this._domainData.entityId,
    );
  }

  protected _createDocumentUploadModalStore(): void {
    if (!this._enrollmentPeriodModule.enrollmentCoreData) {
      throw new Error('Attempted to create a document upload modal store without enrollment period loaded!');
    }
    this.uploadDocumentModalStore = new UploadDocumentModalStore({
      uploadedDocumentsTypes: this._uploadDocumentsModule.documentsTitles,
      onSubmit: this.loadInitialData,
      setPageIsLoading: this._setIsLoading,
      entityId: this._domainData.entityId,
      enrollmentSponsorId: this._enrollmentPeriodModule.enrollmentCoreData.sponsorId,
      groupName: this._group?.name || '',
    });
  }

  protected _createRequestDocumentModalStore(): void {
    this.requestDocumentModalStore = new RequestDocumentModalStore({
      onSubmit: this.loadInitialData,
      entityId: this._domainData.entityId,
      getExistingDocumentTypes: this._uploadDocumentsModule.getExistingDocumentTypes.bind(this),
    });
  }

  protected _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public get activeDocuments(): DocumentDelegate[] {
    return this._uploadDocumentsModule.activeDocuments;
  }

  public get inactiveDocuments(): DocumentDelegate[] {
    return this._uploadDocumentsModule.inactiveDocuments;
  }

  public get isPageLoading(): boolean {
    return this._uploadDocumentsModule.isLoading || this._isLoading;
  }

  public get group(): Pick<Group, 'name' | 'id'> {
    return this._group || { name: '', id: '' };
  }

  /* This concerns the question of compliance with the domain-imposed restrictions. */
  public get pageDomainCompletion(): PageCompletion {
    return {
      allowed: this._uploadDocumentsModule.documentsPassDomainRestrictions,
      notAllowedMessage: `Some documents' restrictions have not been met.`,
    };
  }

  constructor(domainData: UploadDocumentsDomainData, uploadStep: UploadStep) {
    this._domainData = domainData;
    this._uploadDocumentsModule = new UploadDocumentsModule({
      entityId: this._domainData.entityId,
      uploadStep,
      affiliatedId: domainData.affiliatedId,
    });
    makeObservable<
      UploadDocumentsPageStore,
      | '_isLoading'
      | '_setIsLoading'
      | '_group'
      | '_createDocumentUploadModalStore'
      | '_createRequestDocumentModalStore'
      | '_uploadEnrollmentData'
      | '_fetchGroupInfo'
    >(this, {
      _isLoading: observable,
      _group: observable,
      uploadDocumentModalStore: observable,
      requestDocumentModalStore: observable,
      activeDocuments: computed,
      inactiveDocuments: computed,
      isPageLoading: computed,
      pageDomainCompletion: computed,
      group: computed,
      _uploadEnrollmentData: action.bound,
      _setIsLoading: action.bound,
      _createRequestDocumentModalStore: action.bound,
      _createDocumentUploadModalStore: action.bound,
      _fetchGroupInfo: action.bound,
      loadInitialData: action.bound,
    });
  }
}
